import React, { useState, useEffect } from 'react';

const ProfilePage = () => {
  const [user, setUser] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });

  const [editMode, setEditMode] = useState({
    phone: false,
    password: false,
  });

  const [editedData, setEditedData] = useState({
    phone: '',
    password: '',
    confirmPassword: '',
  });

  const [passwordError, setPasswordError] = useState('');
  const [autoDeleteEnabled, setAutoDeleteEnabled] = useState(false);
  const [disableLiveTranscriptAudio, setDisableLiveTranscriptAudio] = useState(false);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser({
        name: storedUser.name || '',
        email: storedUser.email || '',
        phone: storedUser.phone || '',
        password: storedUser.password || '',
      });

      setEditedData({
        phone: storedUser.phone || '',
        password: storedUser.password || '',
        confirmPassword: storedUser.password || '',
      });

      setAutoDeleteEnabled(storedUser.autoDeleteEnabled || false);
      setDisableLiveTranscriptAudio(storedUser.disableLiveTranscriptAudio || false);
    }
  }, []);

  const handleEditToggle = (field) => {
    setEditMode((prev) => ({ ...prev, [field]: !prev[field] }));
    if (field === 'password') setPasswordError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData((prev) => ({ ...prev, [name]: value }));

    if ((name === 'password' || name === 'confirmPassword') && editMode.password) {
      if (
        (name === 'password' && value !== editedData.confirmPassword) ||
        (name === 'confirmPassword' && value !== editedData.password)
      ) {
        setPasswordError('Passwords do not match');
      } else {
        setPasswordError('');
      }
    }
  };

  const handleAutoDeleteChange = (e) => {
    const enabled = e.target.checked;
    setAutoDeleteEnabled(enabled);

    const storedUser = JSON.parse(localStorage.getItem('user')) || {};
    const updatedUser = { ...storedUser, autoDeleteEnabled: enabled };
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const handleDisableAudioChange = (e) => {
    const disabled = e.target.checked;
    setDisableLiveTranscriptAudio(disabled);
    const storedUser = JSON.parse(localStorage.getItem('user')) || {};
    const updatedUser = { ...storedUser, disableLiveTranscriptAudio: disabled };
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const handleSave = () => {
    if (editMode.password && editedData.password !== editedData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    const updatedUser = {
      ...user,
      phone: editedData.phone,
      password: editedData.password,
      autoDeleteEnabled,
    };

    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    setEditMode({ phone: false, password: false });
    setPasswordError('');

    alert('Profile updated successfully!');
    // TODO: Call backend API here to update phone/password in DB
  };

  return (
    <div className='flex justify-center items-center min-h-screen bg-gray-100 px-4'>
      <div className='bg-white shadow-xl rounded-xl w-full max-w-md p-6'>
        <h2 className='text-2xl font-bold text-center text-indigo-600 mb-6'>
          My Profile
        </h2>

        {/* Name */}
        <div className='mb-4'>
          <label className='block font-medium text-gray-700 mb-1'>Full Name</label>
          <input
            type='text'
            value={user.name}
            disabled
            className='w-full px-4 py-2 border rounded-md bg-gray-100'
          />
        </div>

        {/* Email */}
        <div className='mb-4'>
          <label className='block font-medium text-gray-700 mb-1'>Email</label>
          <input
            type='email'
            value={user.email}
            disabled
            className='w-full px-4 py-2 border rounded-md bg-gray-100'
          />
        </div>

        {/* Phone Number */}
        <div className='mb-4'>
          <label className='block font-medium text-gray-700 mb-1'>Phone Number</label>
          <div className='flex'>
            <input
              type='text'
              name='phone'
              value={editedData.phone}
              onChange={handleInputChange}
              disabled={!editMode.phone}
              className='flex-1 px-4 py-2 border rounded-l-md bg-gray-100'
            />
            <button
              onClick={() => handleEditToggle('phone')}
              className='bg-indigo-500 text-white px-4 py-2 rounded-r-md'
            >
              {editMode.phone ? 'Cancel' : 'Edit'}
            </button>
          </div>
        </div>

        {/* Password */}
        <div className='mb-4'>
          <label className='block font-medium text-gray-700 mb-1'>New Password</label>
          <div className='flex'>
            <input
              type='password'
              name='password'
              value={editedData.password}
              onChange={handleInputChange}
              disabled={!editMode.password}
              className='flex-1 px-4 py-2 border rounded-l-md bg-gray-100'
            />
            <button
              onClick={() => handleEditToggle('password')}
              className='bg-indigo-500 text-white px-4 py-2 rounded-r-md'
            >
              {editMode.password ? 'Cancel' : 'Edit'}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        {editMode.password && (
          <div className='mb-4'>
            <label className='block font-medium text-gray-700 mb-1'>Confirm Password</label>
            <input
              type='password'
              name='confirmPassword'
              value={editedData.confirmPassword}
              onChange={handleInputChange}
              className='w-full px-4 py-2 border rounded-md bg-gray-100'
            />
            {passwordError && (
              <p className='text-red-500 text-sm mt-1'>{passwordError}</p>
            )}
          </div>
        )}

        {/* Auto-Delete Checkbox */}
        <div className='mb-6'>
          <div className='flex items-center space-x-3'>
            <input
              type='checkbox'
              id='autoDelete'
              checked={autoDeleteEnabled}
              onChange={handleAutoDeleteChange}
              className='w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2'
            />
            <label htmlFor='autoDelete' className='font-medium text-gray-700'>
              Auto-Delete Notes
            </label>
          </div>
          <p className='text-sm text-gray-500 mt-1 ml-7'>
            When enabled, new notes will not be saved to your account or appear on the home page.
          </p>
        </div>

        {/* Disable Audio for Live Transcript Checkbox */}
        <div className='mb-6'>
          <div className='flex items-center space-x-3'>
            <input
              type='checkbox'
              id='disableLiveTranscriptAudio'
              checked={disableLiveTranscriptAudio}
              onChange={handleDisableAudioChange}
              className='w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2'
            />
            <label htmlFor='disableLiveTranscriptAudio' className='font-medium text-gray-700'>
              Disable Audio for Live Transcript
            </label>
          </div>
          <p className='text-sm text-gray-500 mt-1 ml-7'>
            When enabled, audio will not be sent when sharing a live transcript (public or restricted).
          </p>
        </div>

        {/* Save Button */}
        {(editMode.phone || editMode.password) && (
          <button
            onClick={handleSave}
            disabled={!!passwordError}
            className='w-full bg-green-600 hover:bg-green-500 text-white py-2 rounded-md mt-4'
          >
            Save Changes
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
